// ============================================================================
// Canonical Fleet index route contract
// ============================================================================
//
// EquipmentHome owns a broad /fleet/* compatibility space. The Fleet list and
// Fuel Log are one permanent surface, but every other suffix still has a
// distinct renderer or historical fallback. This narrow, neutral predicate is
// shared by the resolver and release boundary so adding the ledger row cannot
// accidentally authorize /fleet/, a record, an entry namespace, or an alias.
// ============================================================================

export const FLEET_INDEX_PATHNAME = '/fleet';
export const FLEET_FUEL_LOG_PATHNAME = '/fleet/fuel-log';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

/**
 * The two routes rendered by EquipmentHome as equipment.fleet.index. Search
 * and hash remain presentation state: neither Fleet nor Fuel Log reads them,
 * and React Router already carries them separately from pathname.
 */
export function isCanonicalFleetIndexPathname(pathname: unknown): pathname is string {
  return pathname === FLEET_INDEX_PATHNAME || pathname === FLEET_FUEL_LOG_PATHNAME;
}

/**
 * Prove the raw browser location before the resolver consults the ledger.
 * A bare string must be exactly a pathname; only a real location object may
 * carry the independently harmless search/hash presentation state.
 */
export function isCanonicalFleetIndexLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalFleetIndexPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    isCanonicalFleetIndexPathname(pathname) &&
    (search === undefined || typeof search === 'string') &&
    (hash === undefined || typeof hash === 'string')
  );
}
